/**
 * @file OrderPaymentCreateDialog.tsx
 * @description Окно «Зафиксировать платёж» (phase10; phase19.31 — вынесено
 * из `OrderPaymentsTab`, минимум по SC50: способ — `SelectField` вместо
 * нативного списка, на телефоне окно на весь экран, ₽ вместо кода валюты).
 *
 * Вкладку оплат целиком перестраивает phase20 — логика окна не менялась.
 */

import { useState } from "react";

import { Alert } from "../ui/alert";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { useNotify } from "../ui/NotifyProvider";
import { SelectField } from "../ui/SelectField";
import { Textarea } from "../ui/textarea";
import { ApiError } from "../../lib/api-client";
import { PAYMENT_METHOD_LABEL, recordPayment, type OrderDto, type PaymentMethod } from "../../lib/api-orders";

const METHOD_OPTIONS = (Object.entries(PAYMENT_METHOD_LABEL) as [PaymentMethod, string][]).map(([value, label]) => ({
  value,
  label,
}));

interface Props {
  order: OrderDto;
  /** Остаток к оплате — подставляется в сумму. */
  balance: number;
  onClose: () => void;
  onCreated: () => void;
}

export function OrderPaymentCreateDialog({ order, balance, onClose, onCreated }: Props) {
  const notify = useNotify();
  const [method, setMethod] = useState<PaymentMethod>("CASH");
  const [amount, setAmount] = useState(balance > 0 ? balance.toFixed(2) : "0.00");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit() {
    setError(null);
    if (!amount || Number(amount) <= 0) {
      setError("Сумма должна быть больше нуля.");
      return;
    }
    setSaving(true);
    try {
      await recordPayment(order.id, { method, amount, notes: notes.trim() || undefined });
      notify.success("Платёж зафиксирован");
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось зафиксировать платёж");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent fullScreen className="max-w-md">
        <DialogHeader>
          <DialogTitle>Зафиксировать платёж</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          {error ? <Alert severity="error">{error}</Alert> : null}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="pay-method">Способ оплаты</Label>
            <SelectField
              id="pay-method"
              ariaLabel="Способ оплаты"
              value={method}
              onChange={(value) => setMethod(value as PaymentMethod)}
              options={METHOD_OPTIONS}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="pay-amount">Сумма, ₽</Label>
            <Input id="pay-amount" type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="pay-notes">Заметки</Label>
            <Textarea id="pay-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Отмена
          </Button>
          <Button onClick={() => void onSubmit()} disabled={saving} loading={saving}>
            Зафиксировать
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
